import * as THREE from 'three';
import { materialPool } from './MaterialPool';
import { ExplosionSystem } from './Explosion';

// ============================================================
// Flare countermeasures — hot decoys that missiles chase and hit
// ============================================================

const FLARE_COUNT = 3;
const FLARE_SPACING = 0.15;
const FLARE_LIFETIME = 5;
const FLARE_HITBOX = 25;
const FLARE_DRAG = 0.95;
const SPARK_COUNT = 8;         // sparks per flare per frame
const SPARK_LIFETIME = 0.6;

const flareGeom = new THREE.SphereGeometry(4, 8, 8);
const sparkGeom = new THREE.SphereGeometry(0.5, 3, 3);

interface Spark {
  readonly mesh: THREE.Mesh;
  readonly velocity: THREE.Vector3;
  age: number;
}

interface ActiveFlare {
  readonly mesh: THREE.Mesh;
  readonly light: THREE.PointLight;
  readonly velocity: THREE.Vector3;
  age: number;
}

const _backward = new THREE.Vector3();

export class FlareSystem {
  private readonly scene: THREE.Scene;
  private readonly explosions: ExplosionSystem;
  private readonly flares: ActiveFlare[] = [];
  private readonly sparks: Spark[] = [];
  private dropQueue = 0;
  private dropTimer = 0;
  private dropVelocity = new THREE.Vector3();
  private dropPosition = new THREE.Vector3();

  constructor(scene: THREE.Scene, explosions: ExplosionSystem) {
    this.scene = scene;
    this.explosions = explosions;
  }

  /** Start dropping a burst of flares behind the aircraft */
  deploy(aircraft: THREE.Object3D): void {
    _backward.set(0, 0, 1).applyQuaternion(aircraft.quaternion);
    this.dropVelocity.copy(_backward).multiplyScalar(60);
    this.dropPosition.copy(aircraft.position);
    this.dropQueue = FLARE_COUNT;
    this.dropTimer = 0;
  }

  /** Check if any missile position hits a flare — returns true if destroyed */
  checkMissileHit(missilePos: THREE.Vector3): boolean {
    for (const flare of this.flares) {
      const dist = missilePos.distanceTo(flare.mesh.position);
      if (dist < FLARE_HITBOX) {
        // Flare absorbs the missile — both explode
        this.explosions.spawn(missilePos.clone());
        return true;
      }
    }
    return false;
  }

  update(aircraft: THREE.Object3D, dt: number): void {
    // Drop queued flares one by one
    if (this.dropQueue > 0) {
      this.dropTimer -= dt;
      if (this.dropTimer <= 0) {
        this.dropTimer = FLARE_SPACING;
        this.dropQueue--;
        this.spawnFlare(aircraft);
      }
    }

    // Update active flares
    for (let i = this.flares.length - 1; i >= 0; i--) {
      const f = this.flares[i];
      f.age += dt;

      if (f.age >= FLARE_LIFETIME) {
        this.scene.remove(f.mesh);
        this.scene.remove(f.light);
        materialPool.release(f.mesh.material as THREE.MeshBasicMaterial);
        this.flares.splice(i, 1);
        continue;
      }

      // Move and slow down
      f.mesh.position.addScaledVector(f.velocity, dt);
      f.velocity.multiplyScalar(FLARE_DRAG);
      f.mesh.position.y -= 5 * dt; // gravity

      // Fade
      const t = f.age / FLARE_LIFETIME;
      (f.mesh.material as THREE.MeshBasicMaterial).opacity = 1 - t;
      f.light.intensity = 8 * (1 - t);
      f.light.distance = 120;

      // Flicker and pulse
      const flicker = 0.7 + Math.random() * 0.6;
      f.mesh.scale.setScalar(flicker * (1 + t * 0.5));

      // Emit sparks
      if (f.age < FLARE_LIFETIME * 0.8) {
        for (let s = 0; s < SPARK_COUNT * dt * 30; s++) {
          this.spawnSpark(f.mesh.position);
        }
      }
    }

    // Update sparks
    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const s = this.sparks[i];
      s.age += dt;
      if (s.age >= SPARK_LIFETIME) {
        this.scene.remove(s.mesh);
        materialPool.release(s.mesh.material as THREE.MeshBasicMaterial);
        this.sparks.splice(i, 1);
        continue;
      }
      s.mesh.position.addScaledVector(s.velocity, dt);
      s.velocity.y -= 30 * dt; // gravity
      const st = s.age / SPARK_LIFETIME;
      (s.mesh.material as THREE.MeshBasicMaterial).opacity = 1 - st;
      s.mesh.scale.setScalar(1 - st * 0.8);
    }
  }

  private spawnSpark(origin: THREE.Vector3): void {
    const colors = [0xff2200, 0xff4400, 0xff6600, 0xffaa00, 0xff0000];
    const color = colors[Math.floor(Math.random() * colors.length)];
    const mat = materialPool.acquire();
    mat.color.set(color);
    const mesh = new THREE.Mesh(sparkGeom, mat);
    mesh.position.copy(origin).add(new THREE.Vector3(
      (Math.random() - 0.5) * 4,
      (Math.random() - 0.5) * 4,
      (Math.random() - 0.5) * 4,
    ));
    mesh.frustumCulled = false;
    this.scene.add(mesh);

    const velocity = new THREE.Vector3(
      (Math.random() - 0.5) * 40,
      (Math.random() - 0.5) * 40,
      (Math.random() - 0.5) * 40,
    );

    this.sparks.push({ mesh, velocity, age: 0 });
  }

  private spawnFlare(aircraft: THREE.Object3D): void {
    _backward.set(0, 0, 1).applyQuaternion(aircraft.quaternion);

    const pos = aircraft.position.clone()
      .addScaledVector(_backward, 8)
      .add(new THREE.Vector3(
        (Math.random() - 0.5) * 6,
        (Math.random() - 0.5) * 4,
        (Math.random() - 0.5) * 6,
      ));

    const mat = materialPool.acquire();
    mat.color.set(0xffaa22);

    const mesh = new THREE.Mesh(flareGeom, mat);
    mesh.position.copy(pos);
    mesh.frustumCulled = false;
    this.scene.add(mesh);

    // Bright orange light
    const light = new THREE.PointLight(0xff6600, 5, 80);
    light.position.copy(pos);
    this.scene.add(light);

    const velocity = _backward.clone().multiplyScalar(40 + Math.random() * 20);
    velocity.y += Math.random() * 10;

    this.flares.push({ mesh, light, velocity, age: 0 });
  }

  get activeCount(): number {
    return this.flares.length;
  }

  dispose(): void {
    for (const f of this.flares) {
      this.scene.remove(f.mesh);
      this.scene.remove(f.light);
      materialPool.release(f.mesh.material as THREE.MeshBasicMaterial);
    }
    this.flares.length = 0;
  }
}
